import { FilterSchema } from '@adc/blueprint'
import _ from 'lodash'

/**
 * Converters between the blueprint `useFilters` app-model (`filterState`) and
 * the DataTable saved-search `SavedSearchFilterClause[]` format persisted in
 * `parameters.filters`.
 *
 * The new DataTable + FilterToolbar pattern drives queries from blueprint
 * `filterState`, but saved searches are persisted in the SAME format the
 * legacy DataTableWrapper used, so migrated grids do not introduce a divergent
 * on-the-wire format. `filterStateToFilterClauses` is the inverse of
 * `buildFilterDSL`; `filterClausesToFilterState` rehydrates the app-model when
 * a saved search is selected.
 *
 * Supported control types: multiSelect, inlineSelect, singleSelect, boolean,
 * and range (number/Date). Range is stored as absolute `[min, max]` values
 * (the legacy CALENDAR-mode equivalent). The legacy grid's relative date
 * presets (TODAY, LAST_WEEK, …) have no blueprint equivalent and are out of
 * scope.
 */

export type FilterAppModel = Record<string, unknown>

export interface SavedSearchFilterClause {
  fields: string[]
  values: string[]
  valuesAsText: string
  dataType?: string
  searchOperation?: { value: string } | null
  setMatch?: boolean
}

const RANGE_OPERATION = { value: 'RANGE' }

const toDateOrNumberString = (value: unknown): string => {
  if (value instanceof Date) return value.toISOString()
  if (value === null || value === undefined) return ''
  return String(value)
}

/** Walk the blueprint filter definition tree the same way buildFilterDSL does. */
const forEachFilterDefinition = (
  filterDefinitions: FilterSchema.FilterGroupDefinitionArray | undefined,
  visit: (definition: FilterSchema.FilterDefinition) => void
): void => {
  if (!filterDefinitions) return
  filterDefinitions.forEach(group => {
    group.items.forEach(item => {
      if (item.itemType === 'section') {
        ;(item as FilterSchema.FilterSectionDefinition).filters.forEach(visit)
      } else {
        visit(item as FilterSchema.FilterDefinition)
      }
    })
  })
}

/**
 * filterState -> SavedSearchFilterClause[] (persisted to parameters.filters).
 *
 * Emptiness is decided with the SAME `_.isEmpty` guard as buildFilterDSL's
 * processFilterDefinition, so a saved search persists exactly the filters that
 * affect the query — no more, no less. The per-control-type mapping below is
 * the filterClause counterpart of processFilterDefinition (buildDSL.tsx); keep
 * the two in sync when adding a control type.
 */
export const filterStateToFilterClauses = (
  filterState: FilterAppModel,
  filterDefinitions: FilterSchema.FilterGroupDefinitionArray | undefined
): SavedSearchFilterClause[] => {
  const clauses: SavedSearchFilterClause[] = []

  forEachFilterDefinition(filterDefinitions, definition => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const def = definition as any
    const value = filterState[def.name]
    if (_.isEmpty(value)) return

    switch (def.controlType) {
      case 'multiSelect':
      case 'inlineSelect': {
        const values = (Array.isArray(value) ? value : [value]).map(String)
        clauses.push({
          fields: [def.name],
          values,
          valuesAsText: values.join(', '),
          dataType: 'STRING',
        })
        break
      }
      case 'singleSelect': {
        clauses.push({
          fields: [def.name],
          values: [String(value)],
          valuesAsText: String(value),
          dataType: 'STRING',
        })
        break
      }
      case 'boolean': {
        const boolStr = value ? 'true' : 'false'
        clauses.push({
          fields: [def.name],
          values: [boolStr],
          valuesAsText: boolStr,
          dataType: 'BOOLEAN',
        })
        break
      }
      case 'range': {
        const { min, max } = (value ?? {}) as { min?: unknown; max?: unknown }
        const values = [toDateOrNumberString(min), toDateOrNumberString(max)]
        if (!values[0] && !values[1]) return
        clauses.push({
          fields: [def.name],
          values,
          valuesAsText: `${values[0]} - ${values[1]}`,
          dataType:
            def.controlValueType === 'Date'
              ? 'DATETIME_AS_LOCAL'
              : 'NUMBER_RANGE',
          searchOperation: RANGE_OPERATION,
        })
        break
      }
      default:
        break
    }
  })

  return clauses
}

/**
 * SavedSearchFilterClause[] -> filterState (hydrates useFilters on load).
 */
export const filterClausesToFilterState = (
  filterClauses: SavedSearchFilterClause[] | undefined,
  filterDefinitions: FilterSchema.FilterGroupDefinitionArray | undefined
): FilterAppModel => {
  const state: FilterAppModel = {}
  if (!filterClauses) return state

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const definitionByName = new Map<string, any>()
  forEachFilterDefinition(filterDefinitions, definition => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    definitionByName.set((definition as any).name, definition)
  })

  filterClauses.forEach(clause => {
    const name = clause.fields?.[0]
    if (!name) return
    const def = definitionByName.get(name)
    if (!def) return

    switch (def.controlType) {
      case 'range': {
        const [minStr, maxStr] = clause.values || []
        const parse = (raw: string): Date | number | null => {
          if (!raw) return null
          return def.controlValueType === 'Date' ? new Date(raw) : Number(raw)
        }
        state[name] = { min: parse(minStr), max: parse(maxStr) }
        break
      }
      case 'singleSelect': {
        const raw = clause.values?.[0]
        state[name] = def.controlValueType === 'number' ? Number(raw) : raw
        break
      }
      case 'boolean': {
        state[name] = clause.values?.[0] === 'true'
        break
      }
      case 'multiSelect':
      case 'inlineSelect':
      default: {
        state[name] = clause.values
        break
      }
    }
  })

  return state
}
